import React, { useEffect, useState } from 'react';
import { fetchWholesaleDashboard } from '../../api/dashboard';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import API_URL from '../../config/api';
import RevenueChart from './RevenueChart';
import UpcomingEvents from '../../components/UpcomingEvents';
import { apiFetch } from '../../utils/apiFetch';
import { useLocation } from 'react-router-dom';
import { getAudience } from '../../config/permissions';
import NotificationBell from '../../components/NotificationBell';
import './Dashboard.css';

function productQty(p) {
  return Number(p.quantity ?? p.stock ?? 0);
}

function productMinStock(p) {
  return Number(p.minStock ?? p.min_stock ?? 10);
}

const Dashboard = () => {
  const location = useLocation();
  const [denied, setDenied] = useState(location.state?.accessDenied === true);
  useEffect(() => {
    if (denied) { const t = setTimeout(() => setDenied(false), 4000); return () => clearTimeout(t); }
  }, [denied]);

  const [stats, setStats]             = useState({ revenueHistory: [] });
  const [orders, setOrders]           = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [lowStockList, setLowStockList] = useState([]);
  const [arAging, setArAging]         = useState({ current: 0, d30: 0, d60: 0, d90plus: 0 });
  const [todayBookings, setTodayBookings] = useState([]);
  const [business, setBusiness]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState(null);
  const [todayStats, setTodayStats]   = useState({
    todayRevenue: 0,
    todayBookingsCount: 0,
    overdueInvoices: 0,
    lowStockProducts: 0,
    expiringProducts: 0,
    last7DaysRevenue: [],
    monthRevenue: 0,
    lastMonthRevenue: 0,
    pendingOrders: 0,
    totalAR: 0,
  });
  const [alerts, setAlerts]           = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [landlordProps, setLandlordProps] = useState([]);

  const { token, role, userName } = useAuth();
  const isAdmin = role === 'Admin';
  const isStaff = role === 'Staff' || role === 'Editor' || role === 'Viewer' || role === 'Manager';
  const navigate = useNavigate();

  // ─── Data fetching (single API round-trip) ───────────────────────────────
  useEffect(() => {
    async function loadData() {
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError(null);
      try {
        const data = await fetchWholesaleDashboard(token);
        setBusiness(data.business || null);
        setTodayStats(prev => ({ ...prev, ...(data.metrics || {}) }));
        setStats({ revenueHistory: data.revenueHistory || [] });
        setOrders(data.recentOrders || []);
        setArAging(data.arAging || { current: 0, d30: 0, d60: 0, d90plus: 0 });
        setTopCustomers(data.topCustomers || []);
        setLowStockList(data.lowStockList || []);
        setTodayBookings(data.todayBookings || []);
        setAlerts((data.alerts || []).map(a => ({
          ...a,
          action: () => navigate(a.path),
        })));
        setSuggestions((data.suggestions || []).map(s => ({
          ...s,
          action: () => navigate(s.path),
        })));
        if (data._fallback) {
          setLoadError(null);
        }

        if (getAudience(data.business?.business_type) === 'landlord') {
          try {
            const lr = await apiFetch(`${API_URL}/landlord/dashboard`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (lr.ok) {
              const ld = await lr.json();
              setLandlordProps(ld.properties || []);
            }
          } catch (e) {
            console.error('landlordDash:', e);
          }
        }
      } catch (e) {
        console.error('Dashboard load failed:', e);
        setLoadError('Could not load dashboard data. Please refresh or try again.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [token, navigate]);

  // ─── Static display data ──────────────────────────────────────────────────
  const monthPct = todayStats.lastMonthRevenue > 0
    ? Math.round(((todayStats.monthRevenue - todayStats.lastMonthRevenue) / todayStats.lastMonthRevenue) * 100)
    : null;

  const kpiCards = [
    {
      label: 'Monthly Revenue',
      value: `£${Number(todayStats.monthRevenue || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      sub: monthPct !== null
        ? monthPct >= 0 ? `+${monthPct}% vs last month` : `${monthPct}% vs last month`
        : 'This month so far',
      theme: 'blue',
      icon: '💰',
      onClick: () => navigate('/analytics'),
    },
    {
      label: 'Open Orders',
      value: todayStats.pendingOrders,
      sub: todayStats.pendingOrders > 0 ? 'Draft / confirmed / processing' : 'All orders fulfilled',
      theme: 'white',
      icon: '🛒',
      onClick: () => navigate('/orders'),
    },
    {
      label: 'Overdue Invoices',
      value: todayStats.overdueInvoices,
      sub: todayStats.overdueInvoices > 0 ? 'Need attention' : 'All clear',
      theme: 'red',
      icon: '🧾',
      onClick: () => navigate('/invoices'),
    },
    {
      label: 'Low Stock Items',
      value: todayStats.lowStockProducts,
      sub: todayStats.lowStockProducts > 0 ? 'Restock needed' : 'All stocked',
      theme: 'teal',
      icon: '📦',
      onClick: () => navigate('/products/list'),
    },
  ];

  const adminActions = [
    { label: 'New Invoice',     icon: '🧾', path: '/invoices/add' },
    { label: 'New Order',       icon: '🛒', path: '/orders/add' },
    { label: 'New Customer',    icon: '👤', path: '/customers/add' },
    { label: 'New Product',     icon: '📦', path: '/products/add' },
    { label: 'Purchase Orders', icon: '📋', path: '/purchase-orders' },
  ];

  const logoInitial = business?.name ? business.name.charAt(0).toUpperCase() : 'S';
  const isLandlord = getAudience(business?.business_type) === 'landlord';

  // ── Landlord KPI computations (de-duplicated by address for HMO) ──────────
  const seenAddresses = new Set();
  let ldOccupied = 0, ldTotal = 0, ldArrears = 0, ldCerts = 0, ldJobs = 0;
  landlordProps.forEach(p => {
    const isNewProp = !seenAddresses.has(p.address);
    if (isNewProp) {
      seenAddresses.add(p.address);
      ldTotal++;
      if (p.tenant_name) ldOccupied++;
      ldCerts += Number(p.expiring_certs) || 0;
      ldJobs  += Number(p.open_maintenance) || 0;
    }
    if (p.tenant_name && !p.rent_paid && Number(p.rent_due) > 0) {
      ldArrears += Number(p.rent_due);
    }
  });
  const ldOccupancyPct = ldTotal > 0 ? Math.round((ldOccupied / ldTotal) * 100) : 0;

  const displayLabel = userName
    ? (userName.includes('@') ? userName.replace(/@.*/, '@...') : userName)
    : '';

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="dash-root">
      <header className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-header-left">
            <div className="dash-logo">
              {business?.logo
                ? <img src={business.logo} alt={business.name || 'Logo'} />
                : logoInitial}
            </div>
            <div>
              <h1 className="dash-title">{business?.name || 'ShahoSuite'}</h1>
              <p className="dash-subtitle">
                {new Date().toLocaleDateString('en-GB', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                })}
              </p>
            </div>
          </div>
          <div className="dash-header-right">
            <div className="dash-user">
              <div className="dash-user-avatar">{(displayLabel || 'U').charAt(0).toUpperCase()}</div>
              {displayLabel && <span className="dash-user-name">{displayLabel}</span>}
              {role && (
                <span className={`dash-role-badge${role === 'Admin' ? ' dash-role-badge--admin' : ''}`}>{role}</span>
              )}
            </div>
            <NotificationBell />
          </div>
        </div>
      </header>

      <div className="dash-body">

        {/* Access denied toast */}
        {denied && (
          <div className="dash-access-denied">
            You don&apos;t have permission to access that page.
          </div>
        )}

        {loadError && (
          <div className="dash-load-error" role="alert">
            {loadError}
          </div>
        )}

        {loading ? (
          <div className="dash-skeleton" aria-busy="true" aria-label="Loading dashboard">
            <div className="dash-kpi-grid">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="dash-skeleton-card dash-skeleton-pulse" />
              ))}
            </div>
            <div className="dash-skeleton-panel dash-skeleton-pulse" style={{ height: 220 }} />
            <div className="dash-two-col">
              <div className="dash-skeleton-panel dash-skeleton-pulse" style={{ height: 180 }} />
              <div className="dash-skeleton-panel dash-skeleton-pulse" style={{ height: 180 }} />
            </div>
            <div className="dash-skeleton-panel dash-skeleton-pulse" style={{ height: 140 }} />
          </div>
        ) : isLandlord ? (
          /* ════════════════════════════════════════════════════════
             LANDLORD DASHBOARD
          ═════════════════════════════════════════════════════════ */
          <>
            {/* ── Section label ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 20px' }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', color: '#6b7280', textTransform: 'uppercase' }}>Portfolio Overview</span>
              <div style={{ flex: 1, height: 1, background: '#f3f4f6' }} />
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
            </div>

            {/* ── 4 KPI stat cards ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>

              {/* Occupancy Rate */}
              <div onClick={() => navigate('/landlord/properties')}
                style={{ borderRadius: 14, padding: '20px 22px', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 55%, #60a5fa 100%)',
                  boxShadow: '0 6px 20px rgba(37,99,235,0.38)',
                  color: 'white' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.88, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 18 }}>🏘️</span> Occupancy Rate
                </div>
                <div style={{ fontSize: 36, fontWeight: 900, margin: '8px 0 4px', letterSpacing: '-1px' }}>{ldOccupancyPct}%</div>
                <div style={{ fontSize: 12, opacity: 0.80 }}>{ldOccupied} of {ldTotal} propert{ldTotal === 1 ? 'y' : 'ies'} let</div>
              </div>

              {/* Total Arrears */}
              <div onClick={() => navigate('/landlord/rent')}
                style={{ borderRadius: 14, padding: '20px 22px', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
                  background: ldArrears > 0
                    ? 'linear-gradient(135deg, #991b1b 0%, #ef4444 55%, #f87171 100%)'
                    : 'linear-gradient(135deg, #059669 0%, #10b981 55%, #34d399 100%)',
                  boxShadow: ldArrears > 0 ? '0 6px 20px rgba(239,68,68,0.42)' : '0 6px 20px rgba(16,185,129,0.35)',
                  color: 'white' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.88, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 18 }}>💷</span> Total Arrears
                </div>
                <div style={{ fontSize: 36, fontWeight: 900, margin: '8px 0 4px', letterSpacing: '-1px' }}>
                  £{ldArrears.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
                <div style={{ fontSize: 12, opacity: 0.80 }}>{ldArrears > 0 ? 'Rent outstanding this month' : 'All rent collected ✓'}</div>
              </div>

              {/* Expiring Certs */}
              <div onClick={() => navigate('/landlord/certificates')}
                style={{ borderRadius: 14, padding: '20px 22px', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
                  background: ldCerts > 0
                    ? 'linear-gradient(135deg, #92400e 0%, #d97706 55%, #fbbf24 100%)'
                    : 'linear-gradient(135deg, #4338ca 0%, #6366f1 55%, #818cf8 100%)',
                  boxShadow: ldCerts > 0 ? '0 6px 20px rgba(217,119,6,0.42)' : '0 6px 20px rgba(99,102,241,0.35)',
                  color: 'white' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.88, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 18 }}>📁</span> Expiring Certs
                </div>
                <div style={{ fontSize: 36, fontWeight: 900, margin: '8px 0 4px', letterSpacing: '-1px' }}>{ldCerts}</div>
                <div style={{ fontSize: 12, opacity: 0.80 }}>{ldCerts > 0 ? 'Due within 60 days' : 'All certificates valid ✓'}</div>
              </div>

              {/* Open Jobs */}
              <div onClick={() => navigate('/landlord/maintenance')}
                style={{ borderRadius: 14, padding: '20px 22px', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
                  background: ldJobs > 0
                    ? 'linear-gradient(135deg, #c2410c 0%, #f97316 55%, #fb923c 100%)'
                    : 'linear-gradient(135deg, #0f766e 0%, #14b8a6 55%, #2dd4bf 100%)',
                  boxShadow: ldJobs > 0 ? '0 6px 20px rgba(249,115,22,0.35)' : '0 6px 20px rgba(20,184,166,0.35)',
                  color: 'white' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.88, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 18 }}>🛠️</span> Open Jobs
                </div>
                <div style={{ fontSize: 36, fontWeight: 900, margin: '8px 0 4px', letterSpacing: '-1px' }}>{ldJobs}</div>
                <div style={{ fontSize: 12, opacity: 0.80 }}>{ldJobs > 0 ? 'Maintenance pending' : 'No open jobs ✓'}</div>
              </div>

            </div>

            {/* ── Quick Links ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 16px' }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', color: '#6b7280', textTransform: 'uppercase' }}>Quick Access</span>
              <div style={{ flex: 1, height: 1, background: '#f3f4f6' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 28 }}>
              {[
                { label: 'Properties',    icon: '🏘️', path: '/landlord/properties',   color: 'linear-gradient(135deg,#1d4ed8,#2563eb,#60a5fa)', shadow: 'rgba(37,99,235,0.38)' },
                { label: 'Rent Tracker',  icon: '💷', path: '/landlord/rent',          color: 'linear-gradient(135deg,#059669,#10b981,#34d399)', shadow: 'rgba(16,185,129,0.38)' },
                { label: 'Maintenance',   icon: '🛠️', path: '/landlord/maintenance',   color: 'linear-gradient(135deg,#c2410c,#f97316,#fb923c)', shadow: 'rgba(249,115,22,0.38)' },
                { label: 'Certificates', icon: '📁', path: '/landlord/certificates',  color: 'linear-gradient(135deg,#6d28d9,#7c3aed,#a78bfa)', shadow: 'rgba(124,58,237,0.38)' },
                { label: 'Tenants',       icon: '👤', path: '/landlord/tenants',       color: 'linear-gradient(135deg,#0e7490,#0891b2,#22d3ee)', shadow: 'rgba(8,145,178,0.38)' },
                { label: 'Accounting',    icon: '💰', path: '/landlord/accounting',    color: 'linear-gradient(135deg,#be185d,#db2777,#f472b6)', shadow: 'rgba(219,39,119,0.38)' },
              ].map((a, i) => (
                <button key={i} onClick={() => navigate(a.path)}
                  className="action-btn"
                  style={{ background: a.color, boxShadow: `0 6px 18px ${a.shadow}` }}
                >
                  <span style={{ fontSize: 22 }}>{a.icon}</span>
                  {a.label}
                </button>
              ))}
            </div>

            {/* ── Property list mini-summary ── */}
            {ldTotal > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 16px' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', color: '#6b7280', textTransform: 'uppercase' }}>Your Properties</span>
                  <div style={{ flex: 1, height: 1, background: '#f3f4f6' }} />
                  <button onClick={() => navigate('/landlord/properties')} style={{ fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View all →</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                  {Array.from(new Map(landlordProps.map(p => [p.address, p])).values()).slice(0, 6).map((p, i) => {
                    const occupied = landlordProps.filter(r => r.address === p.address && r.tenant_name).length > 0;
                    const arrears  = landlordProps.filter(r => r.address === p.address && r.tenant_name && !r.rent_paid && Number(r.rent_due) > 0).reduce((s, r) => s + Number(r.rent_due), 0);
                    return (
                      <div key={i} onClick={() => navigate('/landlord/properties')}
                        style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, border: '1px solid #e5e7eb', cursor: 'pointer', background: 'white', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}
                      >
                        <span style={{ fontSize: 20 }}>{p.property_type === 'Flat' ? '🏢' : p.property_type === 'HMO' ? '🏠' : '🏘️'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{p.address}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>{p.property_type} · {p.bedrooms} bed{p.bedrooms !== 1 ? 's' : ''}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          {arrears > 0 && (
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#fee2e2', color: '#b91c1c' }}>£{arrears.toLocaleString()} arrears</span>
                          )}
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: occupied ? '#d1fae5' : '#f3f4f6', color: occupied ? '#065f46' : '#6b7280' }}>
                            {occupied ? 'Let' : 'Vacant'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        ) : (
          /* ════════════════════════════════════════════════════════
             STANDARD BUSINESS DASHBOARD
          ═════════════════════════════════════════════════════════ */
          <>

        {/* ── KPI Cards (Admin) ── */}
        {isAdmin && (
          <div className="dash-kpi-grid">
            {kpiCards.map((card, i) => (
              <div
                key={i}
                className={`dash-kpi-card dash-kpi-card--${card.theme}`}
                onClick={card.onClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') card.onClick(); }}
              >
                <div className="dash-kpi-label">
                  <span>{card.icon}</span>
                  {card.label}
                </div>
                <div className="dash-kpi-value">{card.value}</div>
                <div className="dash-kpi-sub">{card.sub}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Needs attention (after KPIs) ── */}
        {isAdmin && alerts.length > 0 && (
          <div className="dash-alerts">
            <span className="dash-alerts-title">Needs attention</span>
            {alerts.map((alert, i) => (
              <button
                key={i}
                type="button"
                className={`dash-alert-chip dash-alert-chip--${alert.tone}`}
                onClick={alert.action}
              >
                {alert.icon} {alert.message}
              </button>
            ))}
          </div>
        )}

        {/* ── Quick actions (under KPIs) ── */}
        <div className="dash-section-label"><span>Quick actions</span></div>
        <div className="dash-panel dash-quick-panel">
          <div className="dash-quick-grid">
            {isAdmin && adminActions.map((a, i) => (
              <button
                key={i}
                type="button"
                onClick={() => navigate(a.path)}
                className="dash-quick-btn"
              >
                <span className="dash-quick-btn-icon">{a.icon}</span>
                {a.label}
              </button>
            ))}
            {isStaff && (
              <button type="button" onClick={() => navigate('/booking')} className="dash-quick-btn">
                <span className="dash-quick-btn-icon">📅</span>
                New Booking
              </button>
            )}
          </div>
        </div>

        {/* ── KPI for Staff (bookings only) ── */}
        {isStaff && !isAdmin && (
          <div className="dash-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', maxWidth: 320 }}>
            <div
              className="dash-kpi-card dash-kpi-card--blue"
              onClick={() => navigate('/booking')}
              role="button"
              tabIndex={0}
            >
              <div className="dash-kpi-label"><span>📅</span> Today&apos;s Bookings</div>
              <div className="dash-kpi-value">{todayStats.todayBookingsCount}</div>
              <div className="dash-kpi-sub">appointments today</div>
            </div>
          </div>
        )}

        {/* ── FINANCE ── */}
        {isAdmin && (
          <div className="dash-section-label"><span>Finance</span></div>
        )}

        {isAdmin && (
          <div className="dash-panel">
            <h2 className="dash-panel-title">Revenue overview</h2>
            <RevenueChart
              data={
                Array.isArray(stats.revenueHistory)
                  ? stats.revenueHistory
                      .map(v => Number(v))
                      .filter(v => typeof v === 'number' && !isNaN(v) && isFinite(v))
                  : []
              }
            />
          </div>
        )}

        {/* ── INTELLIGENCE ── */}
        {isAdmin && (topCustomers.length > 0 || lowStockList.length > 0 || suggestions.length > 0) && (
          <>
            <div className="dash-section-label"><span>Intelligence</span></div>
            <div className="dash-two-col">

              {/* Top Customers */}
              <div className="dash-panel" style={{ marginBottom: 0 }}>
                <div className="dash-panel-header">
                  <h2 className="dash-panel-title">Top customers</h2>
                  <button type="button" className="dash-section-link" onClick={() => navigate('/customers')}>View all</button>
                </div>
                {topCustomers.length === 0
                  ? <p className="dash-empty">No order data yet</p>
                  : topCustomers.map((c, i) => {
                    const pct = topCustomers[0].total > 0 ? (c.total / topCustomers[0].total) * 100 : 0;
                    return (
                      <div key={c.id}
                        onClick={() => navigate('/customers')}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: i < topCustomers.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer' }}
                      >
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e3f2fd', color: '#0074d9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                          <div style={{ height: 4, borderRadius: 2, background: '#f3f4f6', marginTop: 4 }}>
                            <div style={{ height: '100%', borderRadius: 2, background: '#0074d9', width: `${pct}%`, transition: 'width 0.4s' }} />
                          </div>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', flexShrink: 0 }}>£{c.total.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                      </div>
                    );
                  })
                }
              </div>

              {/* Low Stock Priority */}
              <div className="dash-panel" style={{ marginBottom: 0 }}>
                <div className="dash-panel-header">
                  <h2 className="dash-panel-title">Restock priority</h2>
                  <button type="button" className="dash-section-link" onClick={() => navigate('/products')}>View stock</button>
                </div>
                {lowStockList.length === 0
                  ? <div style={{ padding: '12px 14px', borderRadius: 10, background: '#f0fdf4', color: '#065f46', fontSize: 13, fontWeight: 600, border: '1px solid #bbf7d0' }}>All products well stocked</div>
                  : lowStockList.map((p, i) => {
                    const qty = productQty(p);
                    const minSt = productMinStock(p);
                    const urgency = qty === 0 ? '#dc2626' : qty < minSt * 0.3 ? '#ea580c' : '#d97706';
                    return (
                      <div key={p.id}
                        onClick={() => navigate('/products')}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: i < lowStockList.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer' }}
                      >
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f9fafb', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>📦</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>{p.sku ? `SKU: ${p.sku}` : 'No SKU'}</div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 15, color: urgency }}>{qty}</div>
                          <div style={{ fontSize: 10, fontWeight: 600, color: urgency, textTransform: 'uppercase' }}>{qty === 0 ? 'Out' : 'Low'}</div>
                        </div>
                      </div>
                    );
                  })
                }
              </div>

            </div>

            {suggestions.length > 0 && (
              <div className="dash-panel">
                <h2 className="dash-panel-title">Suggested actions</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {suggestions.slice(0, 3).map((s, i) => (
                    <div key={i} className="dash-suggestion" onClick={s.action} role="button" tabIndex={0}>
                      <span>{s.icon}</span>
                      <span className="dash-suggestion-text">{s.text}</span>
                      <span style={{ color: '#9ca3af', fontSize: 14 }}>→</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── ACCOUNTS RECEIVABLE (simple) ── */}
        {isAdmin && todayStats.totalAR > 0 && (
          <>
            <div className="dash-section-label"><span>Accounts receivable</span></div>
            <div className="dash-ar-bar" onClick={() => navigate('/invoices')} role="button" tabIndex={0}>
              <div className="dash-ar-main">
                <span className="dash-ar-label">Outstanding</span>
                <span className="dash-ar-amount">£{todayStats.totalAR.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
              </div>
              <span className="dash-ar-dot">·</span>
              <div className="dash-ar-main">
                <span className="dash-ar-label">Overdue</span>
                <span className="dash-ar-amount dash-ar-amount--warn">£{(arAging.d30 + arAging.d60 + arAging.d90plus).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
              </div>
              <span className="dash-ar-link">View invoices →</span>
            </div>
          </>
        )}

        {/* ── RECENT ACTIVITY (orders, black & white) ── */}
        {isAdmin && (
          <>
            <div className="dash-section-label"><span>Recent activity</span></div>
            <div className="dash-bw-panel">
              <div className="dash-bw-head">
                <span>Recent orders</span>
                <button type="button" className="dash-bw-link" onClick={() => navigate('/orders')}>View all</button>
              </div>
              {orders.length === 0 && <p className="dash-empty" style={{ padding: '16px 18px' }}>No recent orders</p>}
              {orders.map((order, i) => (
                <div
                  key={order.id}
                  className="dash-bw-row"
                  onClick={() => navigate(`/orders/view/${order.id}`)}
                  role="button"
                  tabIndex={0}
                  style={i === orders.length - 1 ? { borderBottom: 'none' } : undefined}
                >
                  <div>
                    <div className="dash-bw-title">Order #{order.id}</div>
                    <div className="dash-bw-meta">{order.customer || 'Customer'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="dash-bw-amount">£{Number(order.total || 0).toFixed(2)}</div>
                    <div className="dash-bw-status">{order.status || 'Pending'}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Operations (bookings & events) ── */}
        {(todayBookings.length > 0) && (
          <>
            <div className="dash-section-label"><span>Operations</span></div>
            <div className="dash-panel">
              <div className="dash-panel-header">
                <h2 className="dash-panel-title">Today&apos;s appointments ({todayBookings.length})</h2>
                <button type="button" className="dash-section-link" onClick={() => navigate('/booking')}>View all</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                {todayBookings.map(b => {
                  const timeRaw = b.start || b.start_time || b.startTime;
                  const time = timeRaw
                    ? new Date(timeRaw).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                    : b.time || 'N/A';
                  return (
                    <div key={b.id} className="dash-bw-row" onClick={() => navigate('/booking')} role="button" tabIndex={0} style={{ borderRadius: 8, marginBottom: 0 }}>
                      <div>
                        <div className="dash-bw-title">{time}</div>
                        <div className="dash-bw-meta">{b.service} · {b.customerName}</div>
                      </div>
                      <div className="dash-bw-status">{b.status}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <div className="dash-panel">
          <UpcomingEvents maxEvents={5} showViewAll={true} />
        </div>

          </>
        )} {/* end standard/landlord ternary */}

      </div>{/* /dash-body */}
    </div>
  );
};

export default Dashboard;